import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Table, TableHead, TableRow, TableCell, TableBody } from '../../components/ui/Table';
import { Search, RefreshCw, Activity, TrendingUp, AlertTriangle, Clock, DollarSign } from 'lucide-react';

interface Trace {
  id: string;
  traceId: string;
  traceType?: string;
  status?: string;
  step?: string;
  durationMs?: number;
  cost?: number;
  createdAt: string;
}

interface TraceMetrics {
  totalTraces: number;
  errorCount: number;
  averageDuration: number;
  errorRate: number;
  costTotal: number;
  tracesByType: Record<string, number>;
}

export const TraceDashboard: React.FC = () => {
  const [traces, setTraces] = useState<Trace[]>([]);
  const [metrics, setMetrics] = useState<TraceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'dashboard' | 'traces'>('dashboard');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load traces
      const tracesResponse = await fetch('/api/traces?limit=50');
      const tracesData = await tracesResponse.json();
      
      // Load metrics
      const metricsResponse = await fetch('/api/traces/analytics/overview');
      const metricsData = await metricsResponse.json();
      
      if (tracesData.success) {
        setTraces(tracesData.data.traces || []);
      }
      
      if (metricsData.success) {
        setMetrics(metricsData.data);
      }
    } catch (error) {
      console.error('Failed to load trace data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      case 'timeout': return 'bg-yellow-500';
      default: return 'bg-neutral-500';
    }
  };

  const formatDuration = (ms: number | undefined) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatCost = (cost: number | string | undefined | null) => {
    if (cost === null || cost === undefined || cost === '') return '-';
    const numCost = typeof cost === 'string' ? parseFloat(cost) : cost;
    if (isNaN(numCost)) return '-';
    return `$${numCost.toFixed(4)}`;
  };

  const filteredTraces = traces.filter(trace =>
    !search || 
    trace.traceId.toLowerCase().includes(search.toLowerCase()) ||
    trace.step?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-neutral-400">Loading trace dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Trace Dashboard</h1>
            <p className="text-neutral-400">Monitor system traces and performance</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant={view === 'dashboard' ? 'primary' : 'outline'}
              onClick={() => setView('dashboard')}
            >
              <Activity className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <Button
              variant={view === 'traces' ? 'primary' : 'outline'}
              onClick={() => setView('traces')}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              All Traces
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-3 text-neutral-400" />
            <Input
              placeholder="Search traces..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Dashboard View */}
        {view === 'dashboard' && metrics && (
          <div className="space-y-6">
            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-blue-900/20 border-blue-700/50">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-300 text-sm font-medium">Total Traces</p>
                      <p className="text-2xl font-bold text-white">{metrics.totalTraces}</p>
                    </div>
                    <Activity className="w-8 h-8 text-blue-400" />
                  </div>
                </div>
              </Card>

              <Card className="bg-red-900/20 border-red-700/50">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-red-300 text-sm font-medium">Error Rate</p>
                      <p className="text-2xl font-bold text-white">{metrics.errorRate.toFixed(1)}%</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 text-red-400" />
                  </div>
                </div>
              </Card>

              <Card className="bg-yellow-900/20 border-yellow-700/50">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-yellow-300 text-sm font-medium">Avg Duration</p>
                      <p className="text-2xl font-bold text-white">{formatDuration(metrics.averageDuration)}</p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-400" />
                  </div>
                </div>
              </Card>

              <Card className="bg-green-900/20 border-green-700/50">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-300 text-sm font-medium">Total Cost</p>
                      <p className="text-2xl font-bold text-white">{formatCost(metrics.costTotal)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-400" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Traces by Type */}
            <Card className="bg-neutral-800/50 border-neutral-700">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Traces by Type</h3>
                <div className="space-y-3">
                  {Object.entries(metrics.tracesByType).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <span className="text-neutral-300 capitalize">{type.replace('_', ' ')}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-24 bg-neutral-700 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${(count / metrics.totalTraces) * 100}%` }}
                          />
                        </div>
                        <span className="text-white font-medium w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Traces View */}
        {view === 'traces' && (
          <Card className="bg-neutral-800/50 border-neutral-700">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">All Traces</h3>
                <span className="text-neutral-400 text-sm">{filteredTraces.length} traces</span>
              </div>
              
              <div className="overflow-x-auto">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Trace ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Step</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Cost</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTraces.map((trace) => (
                      <TableRow key={trace.id}>
                        <TableCell>
                          <span className="font-mono text-xs text-blue-400">{trace.traceId}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-neutral-700 text-neutral-300">
                            {trace.traceType || 'Unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-white ${getStatusColor(trace.status || 'unknown')}`}>
                            {trace.status || 'Unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-neutral-300 text-sm truncate max-w-32">
                            {trace.step || '-'}
                          </span>
                        </TableCell>
                        <TableCell>{formatDuration(trace.durationMs)}</TableCell>
                        <TableCell>{formatCost(trace.cost)}</TableCell>
                        <TableCell>
                          <span className="text-neutral-400 text-sm">
                            {new Date(trace.createdAt).toLocaleString()}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {filteredTraces.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-neutral-400">No traces found.</p>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}; 